import React, { useState, useEffect, useMemo } from 'react';
import { TableCell, Checkbox, TableRow, useTheme } from '@mui/material';
import { SxProps } from '@mui/system';
import CellValue, { Cell, CustomInlineEditors, editable, ErasedEditable } from './CellValue';
import { DataGridColumn } from './utils';
import { getTablePreferences, isDeepEqual } from 'components/utils';
import { stickyCellSx, getOpaqueHoverColor, getOpaqueSelectedColor, getLeadingStickyFallbackWidth } from './utils';
import { Item } from '../DataGrid';
import InlineEditableCell from './InlineEditableCell';
import RecordOpenCell, { OpenInPreference, OpenRecordConfig } from './RecordOpenCell';
import {
  InlineTextEditor,
  InlineTextareaEditor,
  InlineEmailsEditor,
  InlinePhonesEditor,
  InlineClientEditor,
  InlineCandidateEditor,
  InlineDateEditor,
  InlineSelectEditor,
  InlineNameEditor,
  InlineAddressEditor,
  InlineLinksEditor,
  InlineImageEditor,
} from './InlineEditors';

type RenderRowProps<T extends Item> = {
  sx?: SxProps;
  tableName: string;
  item: T;
  selectedObjects: T[];
  setSelectedObjects: React.Dispatch<React.SetStateAction<T[]>>;
  isRowDisabled: (object: T) => boolean;
  cellValuesMap: (item: T, fetchList: () => void, search: string) => Record<string, Cell>;
  getColumns: () => DataGridColumn[];
  fetchList: () => void;
  onCellSaved?: () => void;
  onRowClick: (object: T, fetchList: () => void) => void;
  selectedRowId?: string;
  columnWidths?: Record<string, number>;
  onInlineSave?: (item: T, fieldName: string, value: unknown) => Promise<void>;
  customInlineEditors?: CustomInlineEditors;
  rowTestId?: string;
  openRecord?: OpenRecordConfig<T>;
  openIn?: OpenInPreference;
};

type OverridesPatcher = (
  prev: Record<string, unknown>,
  v: unknown,
  saveFieldName: string,
  item: unknown,
) => Record<string, unknown>;

// When an array is edited, the "primary" reference may disappear (e.g. user
// edits the text of the primary row). Re-pick by row index so primary follows
// the row. Returns:
//   - undefined: primary still present (no shift needed)
//   - string:    new primary value
//   - null:      primary's row was removed
function shiftPrimaryByIndex<T>(
  newArr: T[],
  oldArr: T[],
  oldPrimary: string | undefined,
  key: keyof T,
): string | null | undefined {
  if (!oldPrimary) return undefined;
  if (newArr.some(it => (it[key] as unknown) === oldPrimary)) return undefined;
  const idx = oldArr.findIndex(it => (it[key] as unknown) === oldPrimary);
  return idx >= 0 && idx < newArr.length ? (newArr[idx][key] as unknown as string) : null;
}

// Editor keys that own a sibling "primary" field on the row.
const PRIMARY_CONFIG = {
  phones: { primaryField: 'primaryPhone', subKey: 'phone' as const },
  emails: { primaryField: 'primaryEmail', subKey: 'email' as const },
} as const;

const OVERRIDES_PATCHER: Record<string, OverridesPatcher> = {
  client: (prev, v, saveFieldName) => {
    const { clientId, clientName } = v as { clientId: string; clientName: string };
    return { ...prev, [saveFieldName]: clientName, clientId };
  },
  candidate: (prev, v) => {
    const { candidateId, candidateName } = v as { candidateId: string; candidateName: string };
    return {
      ...prev,
      candidateId: candidateId || null,
      candidate: candidateId ? { _id: candidateId, name: candidateName, firstName: '', lastName: '' } : null,
    };
  },
  name: (prev, v) => {
    const { firstName, lastName } = v as { firstName: string; lastName: string };
    return { ...prev, firstName, lastName };
  },
  date: (prev, v, saveFieldName) => {
    const { iso } = v as { date: Date | null; iso: string };
    return { ...prev, [saveFieldName]: iso || null };
  },
  emails: (prev, v, saveFieldName, item) => patchWithPrimaryShift(prev, v, saveFieldName, item, 'emails'),
  phones: (prev, v, saveFieldName, item) => patchWithPrimaryShift(prev, v, saveFieldName, item, 'phones'),
};

const DEFAULT_CELL_SX = {
  backgroundColor: 'transparent',
};

const EDITOR_LOOKUP: Record<string, (props: any) => React.ReactNode> = {
  text: InlineTextEditor,
  textarea: InlineTextareaEditor,
  emails: InlineEmailsEditor,
  phones: InlinePhonesEditor,
  client: InlineClientEditor,
  candidate: InlineCandidateEditor,
  date: InlineDateEditor,
  select: InlineSelectEditor,
  name: InlineNameEditor,
  address: InlineAddressEditor,
  links: InlineLinksEditor,
  image: InlineImageEditor,
};

const getCellKey = (column: DataGridColumn, index: number) => column.name || `cell-${index}`;

const isRowSelected = <T extends Item>(rows: T[], itemId: string) => rows.some(row => row._id === itemId);

function areRenderRowPropsEqual<T extends Item>(prev: RenderRowProps<T>, next: RenderRowProps<T>) {
  if (prev.sx !== next.sx) return false;
  if (prev.tableName !== next.tableName) return false;
  if (prev.item !== next.item) return false;
  if (prev.setSelectedObjects !== next.setSelectedObjects) return false;
  if (prev.isRowDisabled !== next.isRowDisabled) return false;
  if (prev.cellValuesMap !== next.cellValuesMap) return false;
  if (prev.getColumns !== next.getColumns) return false;
  if (prev.fetchList !== next.fetchList) return false;
  if (prev.onCellSaved !== next.onCellSaved) return false;
  if (prev.onRowClick !== next.onRowClick) return false;
  if (prev.columnWidths !== next.columnWidths) return false;
  if (prev.onInlineSave !== next.onInlineSave) return false;
  if (prev.customInlineEditors !== next.customInlineEditors) return false;
  if (prev.rowTestId !== next.rowTestId) return false;
  if (prev.openRecord !== next.openRecord) return false;
  if (prev.openIn !== next.openIn) return false;
  if (prev.item._id !== next.item._id) return false;

  const wasSelected = isRowSelected(prev.selectedObjects, prev.item._id);
  const isSelected = isRowSelected(next.selectedObjects, next.item._id);
  if (wasSelected !== isSelected) return false;

  const wasHighlighted = prev.selectedRowId === prev.item._id || wasSelected;
  const isHighlighted = next.selectedRowId === next.item._id || isSelected;
  if (wasHighlighted !== isHighlighted) return false;

  return true;
}

function patchWithPrimaryShift(
  prev: Record<string, unknown>,
  v: unknown,
  saveFieldName: string,
  item: unknown,
  editorKey: 'phones' | 'emails',
): Record<string, unknown> {
  const { primaryField, subKey } = PRIMARY_CONFIG[editorKey];
  const newArr = (v as Record<string, string>[]) ?? [];
  const oldArr = ((prev[editorKey] ?? (item as any)[editorKey]) as Record<string, string>[]) ?? [];
  const oldPrimary = (prev[primaryField] as string) ?? (item as any)[primaryField];
  const shifted = shiftPrimaryByIndex(newArr, oldArr, oldPrimary, subKey);
  const next: Record<string, unknown> = { ...prev, [saveFieldName]: v };
  if (shifted !== undefined) next[primaryField] = shifted;
  return next;
}

function RenderRow<T extends Item>({
  sx,
  tableName,
  item,
  selectedObjects,
  setSelectedObjects,
  isRowDisabled,
  cellValuesMap,
  getColumns,
  fetchList,
  onCellSaved,
  onRowClick,
  selectedRowId,
  columnWidths,
  onInlineSave,
  customInlineEditors,
  rowTestId,
  openRecord,
  openIn,
}: RenderRowProps<T>) {
  const theme = useTheme();
  const visibleColumns = getColumns();
  const tablePreferences = getTablePreferences(tableName);
  const search = (tablePreferences.search || '') as string;
  const savedColumns = (tablePreferences.columns || []) as DataGridColumn[];
  const savedColumnWidthsByName = useMemo(() => {
    const widthsByName = new Map<string, number>();
    for (const savedColumn of savedColumns) {
      if (savedColumn.name && typeof savedColumn.width === 'number') {
        widthsByName.set(savedColumn.name, savedColumn.width);
      }
    }
    return widthsByName;
  }, [savedColumns]);
  const selectedIdsSet = useMemo(() => new Set((selectedObjects || []).map(obj => obj._id)), [selectedObjects]);

  const [fieldOverrides, setFieldOverrides] = useState<Record<string, unknown>>({});

  useEffect(() => {
    setFieldOverrides(prev => {
      const next: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(prev)) {
        if (!isDeepEqual((item as any)[key], value)) {
          next[key] = value;
        }
      }
      return next;
    });
  }, [item]);

  const effectiveItem = useMemo(() => {
    const keys = Object.keys(fieldOverrides);
    if (keys.length === 0) return item;
    return { ...item, ...fieldOverrides };
  }, [item, fieldOverrides]);

  const renderBodyRowCell = (item: T, cell: Cell, column: DataGridColumn, index: number) => {
    const cellKey = getCellKey(column, index);

    if (!cell && !(openRecord && column.name === openRecord.columnName)) {
      return <TableCell key={cellKey}></TableCell>;
    }

    let resolvedCell = cell;

    if (cell && !(openRecord && column.name === openRecord.columnName) && typeof cell.editable === 'string') {
      const editorKey = cell.editable;
      const customEntry = customInlineEditors?.[editorKey];
      const Editor = EDITOR_LOOKUP[editorKey] ?? customEntry?.editor;
      if (Editor && onInlineSave) {
        const rawValue = cell.rawValue ?? effectiveItem[column.name as keyof T];
        const getExtraFields = (key: string, rowItem: any): Record<string, unknown> | undefined => {
          switch (key) {
            case 'emails':
              return { primaryEmail: rowItem.primaryEmail };
            case 'phones':
              return { primaryPhone: rowItem.primaryPhone };
            case 'image':
              return { name: rowItem.name };
            default:
              return undefined;
          }
        };
        // For dotted column names like "address.city" where the editor handles the whole object,
        // save using the editor key (e.g. "address") instead of the column name.
        const saveFieldName = column.name!.includes('.') ? editorKey : column.name!;
        const erased: ErasedEditable = editable<unknown>({
          rawValue,
          onSave: async (v: unknown) => {
            await onInlineSave(effectiveItem, saveFieldName, v);

            // Phones/emails: if the primary's reference disappeared from the
            // new array (e.g. its text was edited), persist the shifted primary.
            const cfg = PRIMARY_CONFIG[editorKey as keyof typeof PRIMARY_CONFIG];
            if (cfg) {
              const eff = effectiveItem as any;
              const shifted = shiftPrimaryByIndex(
                v as Record<string, string>[],
                (eff[editorKey] ?? []) as Record<string, string>[],
                eff[cfg.primaryField] as string | undefined,
                cfg.subKey,
              );
              if (shifted !== undefined) {
                await onInlineSave(effectiveItem, cfg.primaryField, shifted);
              }
            }

            setFieldOverrides(prev => {
              const patcher = OVERRIDES_PATCHER[editorKey] ?? customEntry?.patchOverrides;
              return patcher ? patcher(prev, v, saveFieldName, item) : { ...prev, [saveFieldName]: v };
            });
          },
          onSaveField: async (fieldName: string, value: unknown) => {
            await onInlineSave(effectiveItem, fieldName, value);
            setFieldOverrides(prev => ({ ...prev, [fieldName]: value }));
          },
          meta: getExtraFields(editorKey, effectiveItem),
          editor: Editor,
          closeOnCommit:
            customEntry?.closeOnCommit !== undefined
              ? customEntry.closeOnCommit
              : editorKey === 'client'
                ? false
                : undefined,
        });
        resolvedCell = { ...cell, editable: erased };
      } else {
        resolvedCell = { ...cell, editable: undefined };
      }
    }

    if (resolvedCell && typeof resolvedCell.value === 'function' && !resolvedCell.editable) {
      const rawValue = resolvedCell.rawValue ?? effectiveItem[column.name as keyof T];
      resolvedCell = { ...resolvedCell, value: resolvedCell.value(rawValue) };
    }

    // Use real-time width if available, otherwise use saved or initial width
    const columnWidth =
      columnWidths?.[column.name!] ??
      (() => {
        const savedColumnWidth = column.name ? savedColumnWidthsByName.get(column.name) : undefined;
        const fallbackLeadingWidth = getLeadingStickyFallbackWidth({ column, visibleColumns });
        return savedColumnWidth ?? column.width ?? fallbackLeadingWidth;
      })();

    const stickySx = column.isRowSelector || column.isSticky ? stickyCellSx({ left: column.stickyLeft }) : {};

    // Sticky opaque paper must win over DEFAULT_CELL_SX transparency, or frozen
    // columns show scrolling cell text underneath.
    const cellSx = {
      ...DEFAULT_CELL_SX,
      ...(cell?.sx || {}),
      ...(columnWidth && { width: `${columnWidth}px`, minWidth: `${columnWidth}px`, maxWidth: `${columnWidth}px` }),
      ...stickySx,
    };

    if (openRecord && column.name === openRecord.columnName) {
      return (
        <RecordOpenCell
          key={cellKey}
          cellSx={cellSx}
          columnWidthPx={columnWidth}
          label={openRecord.getLabel(effectiveItem)}
          href={openRecord.getHref(effectiveItem)}
          onPreview={openRecord.onPreview ? () => openRecord.onPreview?.(effectiveItem) : undefined}
          openIn={openIn}
        />
      );
    }

    if (resolvedCell?.isRowSelector) {
      return (
        <TableCell key={cellKey} sx={cellSx}>
          <Checkbox
            sx={{ m: 0, p: 0, ml: '5px' }}
            checked={selectedIdsSet.has(effectiveItem._id)}
            disabled={isRowDisabled && isRowDisabled(effectiveItem)}
            onChange={e => {
              setSelectedObjects(prevSelectedObjects =>
                e.target.checked
                  ? prevSelectedObjects.some(obj => obj._id === effectiveItem._id)
                    ? prevSelectedObjects
                    : [...prevSelectedObjects, effectiveItem]
                  : prevSelectedObjects.filter(obj => obj._id !== effectiveItem._id),
              );
            }}
          />
        </TableCell>
      );
    }

    if (resolvedCell?.customCell) {
      return <React.Fragment key={cellKey}>{resolvedCell.customCell}</React.Fragment>;
    }

    if (resolvedCell?.editable) {
      return (
        <InlineEditableCell
          key={cellKey}
          cell={resolvedCell as Cell & { editable: ErasedEditable }}
          cellSx={cellSx}
          columnWidthPx={columnWidth}
          onSaved={onCellSaved}
          rowId={effectiveItem._id}
          columnName={column.name!}
          editorKey={typeof cell.editable === 'string' ? cell.editable : undefined}
        />
      );
    }

    return (
      <TableCell key={cellKey} sx={cellSx}>
        <CellValue cell={resolvedCell as Cell} columnWidthPx={columnWidth} />
      </TableCell>
    );
  };

  const cells: React.ReactNode[] = [];

  const allCells = cellValuesMap(effectiveItem, fetchList, search);

  visibleColumns.forEach((column, index) => {
    if (!column.name) {
      return;
    }

    const cell = allCells[column.name];
    cells.push(renderBodyRowCell(effectiveItem, cell, column, index));
  });

  return (
    <TableRow
      data-testid={rowTestId}
      onClick={(e: React.MouseEvent<HTMLElement>) => {
        // Check if the click target is a table row and not a checkbox or action button
        if (e.target instanceof HTMLElement) {
          const targetElement = e.target;
          const isCheckbox = targetElement.tagName === 'INPUT' && targetElement.getAttribute('type') === 'checkbox';
          const isActionButton = targetElement.closest('button') !== null;
          const isInsideLink = targetElement.closest('a') !== null;

          // If clicking on a checkbox or action button, don't trigger row click
          if (isCheckbox || isActionButton || isInsideLink) {
            e.stopPropagation();
            return;
          }

          // If the row is disabled, prevent click
          if (isRowDisabled && isRowDisabled(item)) {
            e.stopPropagation();
            return;
          }

          if (onRowClick) {
            onRowClick(item, fetchList);
          }
        }
      }}
      sx={{
        '&:last-child td, &:last-child th': { border: 0 },
        cursor: 'pointer',
        '&:hover td': {
          backgroundColor: getOpaqueHoverColor(theme.palette.mode),
        },
        ...(selectedRowId === effectiveItem._id || selectedIdsSet.has(effectiveItem._id)
          ? { '& td': { backgroundColor: getOpaqueSelectedColor(theme.palette.mode) } }
          : {}),
        ...(sx || {}),
      }}
    >
      {cells}
    </TableRow>
  );
}

const MemoizedRenderRow = React.memo(RenderRow, areRenderRowPropsEqual) as typeof RenderRow;

export default MemoizedRenderRow;
